import React, { useState, useCallback, useRef } from "react";
import { toast } from "react-hot-toast";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import Success360Service from "../../Services/Success360Service";

export default function AddSuccess360Banner({
  show,
  onHide,
  onSuccess,
  pageTitle = "Success360 Banner",
}) {
  const [validated, setValidated] = useState(false);
  const imageRef = useRef(null);
  const [bannerData, setBannerData] = useState({
    image: "",
    imageName: "",
    imageType: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  // Handle image upload similar to UploadLogo component
  const handleImageInput = useCallback(async (event) => {
    const { name, type, files, value } = event.target;
    const file = files[0];

    if (file) {
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPEG, PNG)");
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error("File size must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerData((prev) => ({
          ...prev,
          image: reader.result.split(",")[1], // base64 data
          imageName: file.name,
          imageType: file.type,
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setValidated(true);

    if (form.checkValidity() === false) {
      event.stopPropagation();
      return;
    }

    if (!bannerData.image) {
      toast.error("Please select an image file");
      return;
    }

    setIsSaving(true);
    try {
      const loadingMessage = toast.loading("Creating...");

      // Create FormData for file upload
      const formData = new FormData();

      // Convert base64 to File object
      const byteCharacters = atob(bannerData.image);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const file = new File([byteArray], bannerData.imageName, {
        type: bannerData.imageType,
      });

      formData.append("image", file);

      const response = await Success360Service.createSuccess360Banner(formData);

      if (response.data.success === true) {
        toast.success(response.data.message, { id: loadingMessage });
        setBannerData({ image: "", imageName: "", imageType: "" });
        setValidated(false);
        onSuccess && onSuccess();
        onHide();
      } else if (
        response.data.success === false &&
        response.data.response === false
      ) {
        toast.error(response.data.message, { id: loadingMessage });
        setIsSaving(false);
      } else if (
        response.data.success === false &&
        response.data.validationErrors &&
        response.data.validationErrors.length > 0
      ) {
        response.data.validationErrors.map((err) =>
          toast.error(err, { id: loadingMessage })
        );
        setIsSaving(false);
      } else {
        setIsSaving(false);
        toast.error("Something Went Wrong", { id: loadingMessage });
      }
    } catch (error) {
      console.log(error);
      setIsSaving(false);
      toast.error("An error occurred");
    }
  };

  const handleClose = () => {
    setBannerData({ image: "", imageName: "", imageType: "" });
    setValidated(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} className="mt-5" size="lg">
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>Add {pageTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          className="px-3"
        >
          <Row xs={12} md={12} className="g-3">
            <Col xs={12} md={12}>
              <div className="d-flex flex-column justify-content-center">
                <p className="text1-3 text-light-gray mb-0">Banner Image</p>
                <div>
                  {bannerData.image ? (
                    <div className="d-flex mt-2 position-relative">
                      <img
                        src={`data:${bannerData.imageType};base64,${bannerData.image}`}
                        alt="Uploaded"
                        className=""
                        style={{ maxWidth: "90%", maxHeight: "200px" }}
                      />
                      <button
                        className="btn btn-link btn-cross"
                        style={{
                          position: "absolute",
                          right: "35px",
                          top: "-13px",
                        }}
                        onClick={() => {
                          setBannerData((prev) => ({
                            ...prev,
                            image: "",
                            imageName: "",
                            imageType: "",
                          }));
                        }}
                      >
                        <i className="bi bi-x-circle-fill text-danger text2"></i>
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="text1-4 text-dark1 d-flex align-items-center ms-2 mt-2">
                        <label
                          htmlFor="importInput_image"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center">
                            <i className="bi bi-cloud-plus text-dark1 text1-6 d-flex align-items-center justify-content-center me-2"></i>
                            <span className="text1-3">Upload Image</span>
                          </div>
                        </label>

                        <input
                          id="importInput_image"
                          type="file"
                          className="d-none"
                          ref={imageRef}
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleImageInput}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        Please upload a banner image
                      </Form.Control.Feedback>
                    </>
                  )}
                </div>
              </div>
            </Col>

            <Modal.Footer className="mt-4">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSaving}
              >
                Close
              </Button>
              <Button type="submit" variant="success" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </Modal.Footer>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
